// utils/errorHandler.ts
import { AxiosError } from 'axios';
import { logger } from './logger';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

/**
 * Extrai uma mensagem de erro amigável de um erro de API
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    const data = error.response?.data;

    // Mensagens específicas por status HTTP
    switch (status) {
      case 400:
        return data?.message || data?.detail || 'Dados inválidos. Verifique os campos e tente novamente.';
      case 401:
        return 'Não autorizado. Faça login novamente.';
      case 403:
        return 'Você não tem permissão para realizar esta ação.';
      case 404:
        return 'Recurso não encontrado.';
      case 409:
        return data?.message || 'Conflito. Este recurso já existe.';
      case 422:
        return data?.message || 'Erro de validação. Verifique os dados enviados.';
      case 429:
        return 'Muitas requisições. Aguarde alguns minutos e tente novamente.';
      case 500:
        return 'Erro interno do servidor. Tente novamente mais tarde.';
      case 503:
        return 'Serviço temporariamente indisponível. Tente novamente em alguns instantes.';
      default:
        return data?.message || data?.detail || 'Erro inesperado. Tente novamente.';
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Erro desconhecido. Tente novamente.';
};

/**
 * Manipula erros de forma padronizada
 * @param error - O erro capturado
 * @param context - Contexto do erro (ex: "ao buscar fundos", "ao salvar dados")
 * @returns Objeto com informações do erro
 */
export const handleError = (error: unknown, context?: string): ApiError => {
  const message = getErrorMessage(error);
  const fullMessage = context ? `Erro ${context}: ${message}` : message;

  let status: number | undefined;
  let code: string | undefined;
  let details: any;

  if (error instanceof AxiosError) {
    status = error.response?.status;
    code = error.response?.data?.code;
    details = error.response?.data;
  }

  // Log do erro
  logger.error(fullMessage, error);

  return {
    message: fullMessage,
    status,
    code,
    details
  };
};

/**
 * Verifica se o erro é de autenticação
 */
export const isAuthError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    return error.response?.status === 401;
  }
  return false;
};

/**
 * Verifica se o erro é de validação
 */
export const isValidationError = (error: unknown): boolean => {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    return status === 400 || status === 422;
  }
  return false;
};
